/**
 * Outil « Tuiles algébriques » (Boîte à outils, 5e → 2nde).
 *
 * Développer : on règle (ax + b)(cx + d) ; le rectangle se remplit de tuiles
 * x² (bleu), x (vert) et 1 (orange), rouges hachurées quand un seul des deux
 * morceaux est négatif. Les produits partiels s'écrivent à côté, puis la
 * forme réduite (une tuile x verte et une rouge font 0). 5e-4e : simple
 * distributivité (k(x + b), x(x + 4), −(x − 2), 3x(2x + 3)) ; 3e : double
 * distributivité et identités remarquables.
 * Factoriser (dès la 4e : facteur commun ; 3e : identités) : régler les côtés
 * d'un rectangle qui contient exactement les tuiles ; un facteur ±1 ne
 * factorise rien, un facteur encore divisible est signalé. Calculs : `tiles.ts`.
 */

import { useId, useState } from 'react';
import { Lightbulb, Shuffle } from 'lucide-react';
import { schoolLevelRank } from '@jeraisonne/db/cards';
import { MathText, cn } from '@jeraisonne/ui';
import { SendToSheetButton } from '../../send-to-sheet-button';
import type { ToolProps } from '../../tool-types';
import {
  IconButton,
  Note,
  SECONDARY_BUTTON,
  Segmented,
  Stepper,
  ToggleChip,
} from '../../ui/controls';
import { FIGURE_BG_CLASS } from '../../ui/svg-parts';
import {
  A_MAX,
  B_MAX,
  expandSteps,
  factorQuality,
  generateTarget,
  PRESETS,
  polyAtoms,
  polyTex,
  product,
  productTex,
  samePoly,
  tileCounts,
  type Factor,
  type Poly,
} from './tiles';

const X2 = 'var(--color-primary-500)';
const XT = 'var(--color-success-600)';
const ONE = '#c2410c';
const NEG = 'var(--color-error-500)';

const fmtSigned = (v: number) => (v < 0 ? `−${-v}` : String(v));

export function AlgebraTilesTool({
  onSend,
  sendCost,
  level,
  rng = Math.random,
}: ToolProps & { level?: string | null; rng?: () => number }) {
  const rank = Math.min(5, schoolLevelRank(level) ?? 5);
  const [mode, setMode] = useState<'expand' | 'factor'>('expand');
  return (
    <div className="space-y-4">
      {rank >= 3 && (
        <Segmented
          ariaLabel="Mode"
          value={mode}
          options={[
            { value: 'expand', label: 'Développer' },
            { value: 'factor', label: 'Factoriser' },
          ]}
          onChange={setMode}
        />
      )}
      {/* les deux modes restent montés : changer de mode ne perd ni l'exemple ni la cible */}
      <div hidden={mode !== 'expand'} className="space-y-4">
        <Expand rank={rank} onSend={onSend} sendCost={sendCost} />
      </div>
      {rank >= 3 && (
        <div hidden={mode !== 'factor'} className="space-y-4">
          <Factorize rank={rank} rng={rng} onSend={onSend} sendCost={sendCost} />
        </div>
      )}
    </div>
  );
}

/** quatre compteurs : a, b (1er facteur), c, d (2e) ; un facteur n'est jamais nul */
function FactorSteppers({
  f,
  g,
  onChange,
}: {
  f: Factor;
  g: Factor;
  onChange: (f: Factor, g: Factor) => void;
}) {
  // (0, 0) est sauté : un nombre seul passe de 1 à −1 (et inversement) sans s'annuler
  const fix = (h: Factor, prev: Factor): Factor =>
    h.a === 0 && h.b === 0 ? { a: 0, b: prev.b > 0 ? -1 : 1 } : h;
  return (
    <div className="space-y-1">
      <MathText className="text-ink-500 block text-sm">{'$(ax + b)(cx + d)$'}</MathText>
      <div className="grid grid-cols-2 gap-2">
        <Stepper
          label="a"
          value={f.a}
          min={0}
          max={A_MAX}
          onChange={(a) => onChange(fix({ ...f, a }, f), g)}
          format={String}
        />
        <Stepper
          label="b"
          value={f.b}
          min={-B_MAX}
          max={B_MAX}
          onChange={(b) => onChange(fix({ ...f, b }, f), g)}
          format={fmtSigned}
        />
        <Stepper
          label="c"
          value={g.a}
          min={0}
          max={A_MAX}
          onChange={(a) => onChange(f, fix({ ...g, a }, g))}
          format={String}
        />
        <Stepper
          label="d"
          value={g.b}
          min={-B_MAX}
          max={B_MAX}
          onChange={(b) => onChange(f, fix({ ...g, b }, g))}
          format={fmtSigned}
        />
      </div>
    </div>
  );
}

function Expand({
  rank,
  onSend,
  sendCost,
}: {
  rank: number;
  onSend?: ToolProps['onSend'];
  sendCost: number;
}) {
  // 5e-4e : simple distributivité ; 3e : double
  const [f, setF] = useState<Factor>(rank >= 4 ? { a: 1, b: 3 } : { a: 0, b: 3 });
  const [g, setG] = useState<Factor>({ a: 1, b: 2 });
  const [shown, setShown] = useState(false);
  const p = product(f, g);
  const counts = tileCounts(f, g);
  const steps = expandSteps(f, g);
  const set = (nf: Factor, ng: Factor) => {
    setF(nf);
    setG(ng);
    setShown(false);
  };
  return (
    <>
      <div className="flex flex-wrap gap-2" role="group" aria-label="Exemples">
        {PRESETS.filter((q) => q.minRank <= rank).map((q) => (
          <ToggleChip key={q.label} on={samePair(q.f, q.g, f, g)} onClick={() => set(q.f, q.g)}>
            {q.label}
          </ToggleChip>
        ))}
      </div>
      <FactorSteppers f={f} g={g} onChange={set} />
      <MathText className="text-ink-900 block overflow-x-auto text-2xl">{`$${productTex(f, g)}$`}</MathText>
      <TileRect f={f} g={g} />
      <TileLegend counts={counts} />
      <section
        className="border-ink-100 space-y-2 rounded-2xl border px-3 py-3"
        data-testid="tiles-steps"
      >
        {!shown ? (
          <button type="button" className={SECONDARY_BUTTON} onClick={() => setShown(true)}>
            Montrer le calcul
          </button>
        ) : (
          <>
            {steps.map((line, i) => (
              <div key={i} className="space-y-0.5">
                <MathText className="text-ink-900 block overflow-x-auto text-lg">{`$${line.tex}$`}</MathText>
                <p className="text-ink-600 text-sm">{line.say}</p>
              </div>
            ))}
            {counts.xPos > 0 && counts.xNeg > 0 && (
              <p className="text-ink-600 text-sm">
                Une tuile x verte et une tuile x rouge font 0 :{' '}
                {counts.xPos === counts.xNeg
                  ? 'toutes les tuiles x s’annulent par paires.'
                  : `il reste ${Math.abs(counts.xPos - counts.xNeg)} tuile${Math.abs(counts.xPos - counts.xNeg) > 1 ? 's' : ''} x ${counts.xPos > counts.xNeg ? 'verte' : 'rouge'}${Math.abs(counts.xPos - counts.xNeg) > 1 ? 's' : ''}.`}
              </p>
            )}
            {onSend && (
              <div className="flex justify-end">
                <SendToSheetButton
                  label={polyTex(p).replace(/\^2/g, '²').replace(/-/g, '−')}
                  cost={sendCost}
                  onClick={() => onSend({ atoms: polyAtoms(p) })}
                />
              </div>
            )}
          </>
        )}
      </section>
    </>
  );
}

const samePair = (f: Factor, g: Factor, f2: Factor, g2: Factor) =>
  f.a === f2.a && f.b === f2.b && g.a === g2.a && g.b === g2.b;

function Factorize({
  rank,
  rng,
  onSend,
  sendCost,
}: {
  rank: number;
  rng: () => number;
  onSend?: ToolProps['onSend'];
  sendCost: number;
}) {
  const [target, setTarget] = useState<Poly>(
    rank >= 4 ? { x2: 1, x: 6, c: 9 } : { x2: 0, x: 3, c: 6 },
  );
  const [f, setF] = useState<Factor>({ a: 1, b: 1 });
  const [g, setG] = useState<Factor>({ a: 1, b: 1 });
  const [tip, setTip] = useState<string | null>(null);
  const p = product(f, g);
  const ok = samePoly(p, target);
  const quality = factorQuality(f, g);
  const rows: Array<{ name: string; need: number; have: number }> = [
    { name: 'x²', need: target.x2, have: p.x2 },
    { name: 'x', need: target.x, have: p.x },
    { name: '1', need: target.c, have: p.c },
  ];
  const fresh = () => {
    setTarget(generateTarget(rng, rank).target);
    setF({ a: 1, b: 1 });
    setG({ a: 1, b: 1 });
    setTip(null);
  };
  const hintText = () => {
    if (target.x2 === 0)
      return `Pas de tuile x² : un des côtés n’a pas de x, c’est un nombre qui divise ${Math.abs(target.x)} et ${Math.abs(target.c)}.`;
    if (target.c === 0) return 'Pas de tuile 1 : x est un des côtés (on met x en facteur).';
    if (target.x === 0 && target.c < 0)
      return `Les tuiles x s’annulent par paires : les deux nombres sont opposés, et leur produit vaut ${fmtSigned(target.c)}.`;
    return `Les tuiles x² donnent le produit des coefficients de x (${target.x2}) ; les tuiles 1, le produit des deux nombres (${fmtSigned(target.c)}).`;
  };
  return (
    <>
      <div className="flex flex-wrap items-center gap-2">
        <p className="text-ink-700 text-sm">Factoriser :</p>
        <MathText className="text-ink-900 min-w-0 flex-1 overflow-x-auto text-2xl">{`$${polyTex(target)}$`}</MathText>
        <IconButton label="Nouvelle expression" onClick={fresh}>
          <Shuffle className="h-5 w-5" aria-hidden />
        </IconButton>
      </div>
      <p className="text-ink-600 text-sm">
        Règle les côtés du rectangle pour qu’il contienne exactement ces tuiles.
      </p>
      <FactorSteppers
        f={f}
        g={g}
        onChange={(nf, ng) => {
          setF(nf);
          setG(ng);
        }}
      />
      <MathText className="text-ink-900 block overflow-x-auto text-xl">{`$${productTex(f, g)} = ${polyTex(p)}$`}</MathText>
      <TileRect f={f} g={g} />
      <table className="text-ink-900 w-full text-sm tabular-nums" data-testid="tiles-check">
        <thead>
          <tr className="text-ink-600">
            <th className="text-left font-semibold">tuiles</th>
            <th className="text-right font-semibold">il faut</th>
            <th className="text-right font-semibold">ton rectangle</th>
            <th className="w-8">
              <span className="sr-only">résultat</span>
            </th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.name}>
              <td>{r.name}</td>
              <td className="text-right">{fmtSigned(r.need)}</td>
              <td className="text-right">{fmtSigned(r.have)}</td>
              <td
                className={cn(
                  'text-center font-bold',
                  r.need === r.have ? 'text-success-600' : 'text-error-600',
                )}
              >
                {r.need === r.have ? '✓' : '✗'}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      <div aria-live="polite" className="space-y-2">
        {ok && quality.kind !== 'complete' ? (
          <Note live={false}>
            <span data-testid="tiles-partial">
              {quality.kind === 'trivial'
                ? 'C’est bien égal, mais un facteur 1 ne factorise rien : cherche un vrai facteur commun.'
                : `C’est bien égal, mais ce n’est pas fini : on peut encore mettre ${quality.k} en facteur dans une parenthèse.`}
            </span>
          </Note>
        ) : ok ? (
          <div className="bg-success-50 space-y-2 rounded-2xl px-3 py-3" data-testid="tiles-done">
            <MathText className="text-ink-900 block overflow-x-auto text-lg">{`$${polyTex(target)} = ${productTex(f, g)}$`}</MathText>
            <p className="text-success-600 text-sm font-bold">
              Rectangle complet : c’est factorisé.
            </p>
            {onSend && (
              <SendToSheetButton
                label={productTex(f, g).replace(/\^2/g, '²').replace(/-/g, '−')}
                cost={sendCost}
                onClick={() => onSend({ atoms: factorAtoms(f, g) })}
              />
            )}
          </div>
        ) : (
          <>
            <button
              type="button"
              className={cn(SECONDARY_BUTTON, 'inline-flex items-center gap-1.5')}
              onClick={() => setTip(hintText())}
            >
              <Lightbulb className="h-4 w-4" aria-hidden />
              Un indice
            </button>
            {tip && <Note live={false}>{tip}</Note>}
          </>
        )}
      </div>
    </>
  );
}

/** (2x + 3)(x + 2) → ['(', '2', 'x', '+', '3', ')', '(', 'x', '+', '2', ')'] */
function factorAtoms(f: Factor, g: Factor): string[] {
  const one = (h: Factor): string[] => {
    const xs = h.a === 0 ? [] : h.a === 1 ? ['x'] : [String(h.a), 'x'];
    if (!xs.length) return h.b < 0 ? ['(', '−', String(-h.b), ')'] : [String(h.b)];
    if (h.b === 0) return xs;
    return ['(', ...xs, h.b < 0 ? '−' : '+', String(Math.abs(h.b)), ')'];
  };
  if (f.a === g.a && f.b === g.b && f.a && f.b) return [...one(f), '²'];
  return g.a === 0 ? [...one(g), ...one(f)] : [...one(f), ...one(g)];
}

function TileLegend({ counts }: { counts: ReturnType<typeof tileCounts> }) {
  const item = (color: string, label: string, n: number, neg = false) =>
    n > 0 && (
      <span className="flex items-center gap-1">
        <span
          className="inline-block h-3 w-3 rounded-sm"
          style={{ background: neg ? NEG : color }}
          aria-hidden
        />
        {n} {label}
      </span>
    );
  return (
    <p
      className="text-ink-700 flex flex-wrap justify-center gap-x-4 gap-y-1 text-xs font-semibold"
      data-testid="tiles-legend"
    >
      {item(X2, `tuile${counts.x2 > 1 ? 's' : ''} x²`, counts.x2)}
      {item(XT, `tuile${counts.xPos > 1 ? 's' : ''} x`, counts.xPos)}
      {item(XT, `tuile${counts.xNeg > 1 ? 's' : ''} −x`, counts.xNeg, true)}
      {item(ONE, `tuile${counts.onePos > 1 ? 's' : ''} 1`, counts.onePos)}
      {item(ONE, `tuile${counts.oneNeg > 1 ? 's' : ''} −1`, counts.oneNeg, true)}
    </p>
  );
}

/** « (x - 2)^2 » à voix haute : « (x moins 2) au carré » */
const spoken = (tex: string) =>
  tex
    .replace(/\\times/g, 'fois')
    .replace(/\^2/g, ' au carré')
    .replace(/ - /g, ' moins ')
    .replace(/-/g, 'moins ');

const W = 320;
const HEAD = 26;
const L0 = 90;
const U0 = 24;

interface Seg {
  kind: 'x' | '1';
  sign: 1 | -1;
}

const segsOf = (h: Factor): Seg[] => [
  ...Array.from({ length: h.a }, () => ({ kind: 'x' as const, sign: 1 as const })),
  ...Array.from({ length: Math.abs(h.b) }, () => ({
    kind: '1' as const,
    sign: (h.b < 0 ? -1 : 1) as 1 | -1,
  })),
];

/** Le rectangle : 1er facteur sur la hauteur (à gauche), 2e sur la largeur (en haut). */
function TileRect({ f, g }: { f: Factor; g: Factor }) {
  const hatch = `tiles-hatch-${useId().replace(/:/g, '')}`;
  const rows = segsOf(f);
  const cols = segsOf(g);
  const rawW = g.a * L0 + Math.abs(g.b) * U0;
  const rawH = f.a * L0 + Math.abs(f.b) * U0;
  const k = Math.min(1, (W - HEAD - 10) / rawW, 250 / rawH);
  const L = L0 * k;
  const U = U0 * k;
  const len = (s: Seg) => (s.kind === 'x' ? L : U);
  // le rectangle et ses étiquettes, centrés
  const ox = Math.max(0, (W - HEAD - rawW * k) / 2 - 4);
  const xs = cols.map((_, i) => ox + HEAD + cols.slice(0, i).reduce((t, s) => t + len(s), 0));
  const ys = rows.map((_, i) => HEAD + rows.slice(0, i).reduce((t, s) => t + len(s), 0));
  const H = HEAD + rawH * k + 10;
  const anyNeg = f.b < 0 || g.b < 0;
  return (
    <div className="mx-auto w-full max-w-[26rem]">
      <svg
        viewBox={`0 0 ${W} ${H}`}
        className={cn('border-ink-100 block w-full rounded-2xl border', FIGURE_BG_CLASS)}
        role="img"
        aria-label={`Rectangle de côtés ${spoken(productTex(f, g))} : ${spoken(polyTex(product(f, g)))}.`}
        data-testid="tiles-rect"
      >
        <defs>
          <pattern
            id={hatch}
            width="6"
            height="6"
            patternUnits="userSpaceOnUse"
            patternTransform="rotate(45)"
          >
            <line
              x1="0"
              y1="0"
              x2="0"
              y2="6"
              stroke="#ffffff"
              strokeWidth="2"
              strokeOpacity="0.55"
            />
          </pattern>
        </defs>
        {/* les x un par un ; les 1 groupés en un seul nombre (« −6 ») au milieu de leur bande */}
        {cols.map((s, j) =>
          s.kind === 'x' ? (
            <text
              key={`c${j}`}
              x={xs[j]! + len(s) / 2}
              y={HEAD - 8}
              textAnchor="middle"
              fontSize={13}
              fontWeight={700}
              fill="var(--color-ink-800)"
            >
              x
            </text>
          ) : null,
        )}
        {g.b !== 0 && (
          <text
            x={xs[g.a]! + (Math.abs(g.b) * U) / 2}
            y={HEAD - 8}
            textAnchor="middle"
            fontSize={13}
            fontWeight={700}
            fill={g.b < 0 ? 'var(--color-error-600)' : 'var(--color-ink-800)'}
          >
            {g.b > 0 ? `+${g.b}` : `−${-g.b}`}
          </text>
        )}
        {rows.map((s, i) =>
          s.kind === 'x' ? (
            <text
              key={`r${i}`}
              x={ox + HEAD - 6}
              y={ys[i]! + len(s) / 2 + 4}
              textAnchor="end"
              fontSize={13}
              fontWeight={700}
              fill="var(--color-ink-800)"
            >
              x
            </text>
          ) : null,
        )}
        {f.b !== 0 && (
          <text
            x={ox + HEAD - 4}
            y={ys[f.a]! + (Math.abs(f.b) * U) / 2 + 4}
            textAnchor="end"
            fontSize={13}
            fontWeight={700}
            fill={f.b < 0 ? 'var(--color-error-600)' : 'var(--color-ink-800)'}
          >
            {f.b > 0 ? `+${f.b}` : `−${-f.b}`}
          </text>
        )}
        {rows.map((r, i) =>
          cols.map((c, j) => {
            const kind =
              r.kind === 'x' && c.kind === 'x'
                ? 'x2'
                : r.kind === 'x' || c.kind === 'x'
                  ? 'x'
                  : '1';
            const neg = r.sign * c.sign < 0;
            const fill = neg ? NEG : kind === 'x2' ? X2 : kind === 'x' ? XT : ONE;
            const w = len(c);
            const h = len(r);
            return (
              <g key={`${i}-${j}`}>
                <rect
                  x={xs[j]! + 0.75}
                  y={ys[i]! + 0.75}
                  width={w - 1.5}
                  height={h - 1.5}
                  rx={2}
                  fill={fill}
                  fillOpacity={0.9}
                />
                {neg && (
                  <rect
                    x={xs[j]! + 0.75}
                    y={ys[i]! + 0.75}
                    width={w - 1.5}
                    height={h - 1.5}
                    rx={2}
                    fill={`url(#${hatch})`}
                  />
                )}
                {kind === 'x2' && (
                  <text
                    x={xs[j]! + w / 2}
                    y={ys[i]! + h / 2 + 5}
                    textAnchor="middle"
                    fontSize={14}
                    fontWeight={700}
                    fill="#ffffff"
                  >
                    {neg ? '−x²' : 'x²'}
                  </text>
                )}
              </g>
            );
          }),
        )}
      </svg>
      <p className="text-ink-500 mt-1 text-center text-xs">
        Bleu : x² ; vert : x ; orange : 1 ; rouge hachuré : négatif (un seul des deux côtés est
        négatif).
        {anyNeg &&
          ' Avec des −1, on compte les tuiles avec leur signe : ce n’est plus une vraie aire.'}
      </p>
    </div>
  );
}
